import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTicketAlt, FaTrophy, FaCreditCard, FaWallet, FaCoins,
  FaUser, FaPhone, FaEnvelope, FaLayerGroup, FaCheckCircle,
  FaEdit, FaTimes, FaSave, FaCalendarAlt, FaClock,
  FaArrowRight, FaDownload, FaCamera,
} from "react-icons/fa";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import { generateTicketPDF } from "./GenerateTicketpdf";

const BASE_URL = "https://api.dreamtoday.in";
const PER_PAGE = 6;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });

/* ─────────────────────────────────────────────────────────
   PAGINATION
───────────────────────────────────────────────────────── */
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex flex-wrap justify-center mt-6 gap-2 items-center">
      <button disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
        className="px-4 py-2 rounded-lg bg-amber-200 hover:bg-amber-300 disabled:opacity-40 text-sm font-medium">
        Prev
      </button>
      <button onClick={() => onPageChange(1)}
        className={`px-4 py-2 rounded-lg text-sm font-medium ${currentPage === 1 ? "bg-amber-600 text-white" : "bg-amber-200 hover:bg-amber-300"}`}>
        1
      </button>
      {currentPage > 3 && <span className="px-2 py-2 text-amber-700 font-semibold">...</span>}
      {[currentPage - 1, currentPage, currentPage + 1]
        .filter(p => p > 1 && p < totalPages)
        .map(p => (
          <button key={p} onClick={() => onPageChange(p)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${currentPage === p ? "bg-amber-600 text-white" : "bg-amber-200 hover:bg-amber-300"}`}>
            {p}
          </button>
        ))}
      {currentPage < totalPages - 2 && <span className="px-2 py-2 text-amber-700 font-semibold">...</span>}
      {totalPages > 1 && (
        <button onClick={() => onPageChange(totalPages)}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${currentPage === totalPages ? "bg-amber-600 text-white" : "bg-amber-200 hover:bg-amber-300"}`}>
          {totalPages}
        </button>
      )}
      <button disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        className="px-4 py-2 rounded-lg bg-amber-200 hover:bg-amber-300 disabled:opacity-40 text-sm font-medium">
        Next
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   TICKET CARD  (inside TicketsModal)
───────────────────────────────────────────────────────── */
const TicketCard = ({ ticketId, index, campaignCategory, endDate }) => {
  const emoji = { daily: "🔥", weekly: "⚡", monthly: "👑" }[campaignCategory] || "🎟️";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07 }}
      className="relative overflow-hidden rounded-2xl"
      style={{
        background: "linear-gradient(135deg,#FFFDF5,#FFF1CC)",
        border: "1.5px solid #F0D98C",
        boxShadow: "0 4px 16px rgba(201,168,76,0.18)",
      }}
    >
      <div className="h-1.5 w-full"
        style={{ background: "linear-gradient(90deg,#E8C96A,#C9A84C,#A8863A)" }} />
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-5 rounded-r-full"
        style={{ background: "white", border: "1.5px solid #F0D98C", borderLeft: "none" }} />
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-5 rounded-l-full"
        style={{ background: "white", border: "1.5px solid #F0D98C", borderRight: "none" }} />
      <div className="px-6 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-lg"
            style={{ background: "#FFF1CC", border: "1px solid #F0D98C" }}>
            {emoji}
          </div>
          <div className="min-w-0">
            <p className="text-[9px] font-black uppercase tracking-widest" style={{ color: "#B89A5A" }}>Ticket ID</p>
            <p className="font-black text-sm truncate" style={{ color: "#5C4A1E" }}>{ticketId}</p>
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "#B89A5A" }}>Valid till</p>
          <p className="text-xs font-black" style={{ color: "#A8863A" }}>{formatDate(endDate)}</p>
        </div>
      </div>
      <div className="mx-5 border-t border-dashed" style={{ borderColor: "#F0D98C" }} />
      <div className="px-6 py-2 flex items-center justify-between">
        <div className="flex gap-0.5 items-end">
          {Array.from({ length: 22 }).map((_, i) => (
            <div key={i} className="rounded-sm" style={{
              width: i % 3 === 0 ? 3 : 2,
              height: i % 5 === 0 ? 14 : 10,
              background: i % 4 === 0 ? "#A8863A" : "#F0D98C",
              opacity: 0.75,
            }} />
          ))}
        </div>
        <span className="text-[9px] font-black tracking-widest" style={{ color: "#B89A5A" }}>
          #{String(index + 1).padStart(2, "0")}
        </span>
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────────────────
   TICKETS MODAL
───────────────────────────────────────────────────────── */
const TicketsModal = ({ booking, profile, onClose }) => {
  const campaign = booking.campaignId || {};
  const tickets = booking.ticketIds || [];
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const handleDownload = async () => {
    setDownloading(true);
    try { await generateTicketPDF(booking, profile); }
    catch (e) { console.error(e); alert("PDF failed. Ensure jsPDF is installed: npm i jspdf"); }
    finally { setDownloading(false); }
  };

  const STATUS_STYLES = {
    active: { bg: "#DCFCE7", color: "#166534" },
    upcoming: { bg: "#FFF1CC", color: "#A8863A" },
    ended: { bg: "#F3F4F6", color: "#6B7280" },
    booked: { bg: "#EFF6FF", color: "#1D4ED8" },
  };
  const sc = STATUS_STYLES[booking.bookingStatus || booking.status] || { bg: "#FFF1CC", color: "#A8863A" };

  return createPortal(
    <AnimatePresence>
      <motion.div key="bd"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
        style={{ background: "rgba(60,40,5,0.55)", backdropFilter: "blur(6px)" }}
      >
        <motion.div key="pn"
          initial={{ opacity: 0, y: 60, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.97 }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full flex flex-col overflow-hidden"
          style={{
            maxWidth: 520, maxHeight: "92dvh",
            background: "linear-gradient(165deg,#FFFDF5,#FFF8E7 60%,#FFF1CC)",
            border: "1.5px solid #F0D98C", borderRadius: 24,
            boxShadow: "0 24px 80px rgba(80,50,0,0.28)",
          }}
        >
          {/* ── HEADER ── */}
          <div className="flex-shrink-0 px-5 pt-5 pb-4" style={{ borderBottom: "1.5px solid #F0D98C" }}>
            <div className="flex justify-center mb-3 sm:hidden">
              <div className="w-10 h-1 rounded-full" style={{ background: "#F0D98C" }} />
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-14 h-14 rounded-2xl overflow-hidden" style={{ border: "1px solid #F0D98C" }}>
                {campaign.images?.[0]
                  ? <img src={`${BASE_URL}${campaign.images[0]}`} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center" style={{ background: "#FFF1CC" }}>
                    <FaTicketAlt style={{ color: "#E8C96A", fontSize: 20 }} />
                  </div>
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest mb-0.5" style={{ color: "#B89A5A" }}>My Tickets</p>
                <h2 className="font-extrabold leading-tight truncate" style={{ color: "#5C4A1E", fontSize: "clamp(14px,3vw,18px)" }}>
                  {booking.campaignTitle}
                </h2>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold"
                    style={{ background: sc.bg, color: sc.color }}>
                    {(booking.bookingStatus || "booked").toUpperCase()}
                  </span>
                  <span className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#B89A5A" }}>
                    <FaTicketAlt style={{ fontSize: 8 }} />
                    {tickets.length} ticket{tickets.length !== 1 ? "s" : ""}
                  </span>
                  <span className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#B89A5A" }}>
                    <FaCoins style={{ fontSize: 8 }} />₹{booking.totalAmount}
                  </span>
                </div>
              </div>
              <button onClick={onClose}
                className="flex-shrink-0 flex items-center justify-center rounded-full transition hover:scale-105"
                style={{ width: 32, height: 32, background: "#FFF1CC", border: "1px solid #F0D98C" }}>
                <FaTimes style={{ color: "#A8863A", fontSize: 11 }} />
              </button>
            </div>
          </div>

          {/* ── DATE STRIP ── */}
          <div className="flex-shrink-0 grid grid-cols-3" style={{ borderBottom: "1px solid #F0D98C" }}>
            {[
              { Icon: FaCalendarAlt, label: "Booked On", val: formatDate(booking.bookingDate) },
              { Icon: FaCalendarAlt, label: "Start Date", val: formatDate(booking.campaignStartDate) },
              { Icon: FaClock, label: "End Date", val: formatDate(booking.campaignEndDate) },
            ].map(({ Icon, label, val }, i) => (
              <div key={label}
                className={`flex flex-col items-center py-3 px-2 gap-0.5 ${i < 2 ? "border-r" : ""}`}
                style={{ borderColor: "#F0D98C" }}>
                <Icon style={{ color: "#C9A84C", fontSize: 10 }} />
                <span className="text-[9px] font-bold uppercase tracking-wider text-center" style={{ color: "#B89A5A" }}>{label}</span>
                <span className="text-[11px] font-black text-center" style={{ color: "#5C4A1E" }}>{val}</span>
              </div>
            ))}
          </div>

          {/* ── PRIZES ── */}
          {campaign.prizes?.length > 0 && (
            <div className="flex-shrink-0 px-5 py-3" style={{ borderBottom: "1px solid #F0D98C", background: "#FFF8E7" }}>
              <p className="text-[9px] font-black uppercase tracking-widest mb-1.5" style={{ color: "#B89A5A" }}>Prizes</p>
              <div className="flex flex-wrap gap-1.5">
                {campaign.prizes.map(p => (
                  <span key={p._id}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold"
                    style={{ background: "#FFF1CC", color: "#A8863A", border: "1px solid #F0D98C" }}>
                    <FaTrophy style={{ color: "#C9A84C", fontSize: 7 }} />
                    {p.prizeName}{p.description ? ` — ${p.description}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── SCROLLABLE TICKET LIST ── */}
          <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3"
            style={{ scrollbarWidth: "thin", scrollbarColor: "#F0D98C transparent" }}>
            {tickets.length === 0
              ? <div className="flex flex-col items-center justify-center py-12 rounded-2xl"
                style={{ background: "#FFF1CC", border: "1px solid #F0D98C" }}>
                <FaTicketAlt style={{ color: "#E8C96A", fontSize: 32, opacity: 0.4, marginBottom: 10 }} />
                <p className="font-bold text-sm" style={{ color: "#8A6E34" }}>No ticket IDs found</p>
              </div>
              : tickets.map((tid, i) => (
                <TicketCard key={tid} ticketId={tid} index={i}
                  campaignCategory={campaign.campaignCategory}
                  endDate={booking.campaignEndDate} />
              ))
            }
          </div>

          {/* ── FOOTER ── */}
          <div className="flex-shrink-0 px-5 py-4" style={{ borderTop: "1.5px solid #F0D98C" }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold" style={{ color: "#B89A5A" }}>Total Paid</span>
              <span className="text-lg font-black" style={{ color: "#A8863A" }}>₹{booking.totalAmount}</span>
            </div>
            <div className="flex gap-3">
              {/* Download PDF */}
              <motion.button
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                onClick={handleDownload} disabled={downloading}
                className="flex-1 rounded-2xl font-extrabold text-sm py-3 flex items-center justify-center gap-2"
                style={{
                  background: downloading ? "rgba(240,217,140,0.5)" : "linear-gradient(135deg,#FFF1CC,#F0D98C)",
                  color: "#A8863A", border: "1.5px solid #E8C96A",
                  boxShadow: "0 2px 12px rgba(201,168,76,0.18)",
                }}
              >
                {downloading
                  ? <><div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />Generating…</>
                  : <><FaDownload style={{ fontSize: 11 }} />Download PDF</>
                }
              </motion.button>
              {/* Close */}
              <button onClick={onClose}
                className="flex-1 rounded-2xl font-extrabold text-sm py-3 transition hover:scale-[1.01]"
                style={{
                  background: "linear-gradient(135deg,#E8C96A,#C9A84C,#A8863A)",
                  color: "#3B2A0A", boxShadow: "0 4px 16px rgba(201,168,76,0.22)",
                }}>
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

/* ─────────────────────────────────────────────────────────
   CAMPAIGNS TAB
───────────────────────────────────────────────────────── */
const Campaigns = ({ bookings, loading, error, profile }) => {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [dlId, setDlId] = useState(null);

  const totalPages = Math.ceil(bookings.length / PER_PAGE);
  const paged = [...bookings]
    .slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const directDownload = async (e, b) => {
    e.stopPropagation();
    setDlId(b._id);
    try { await generateTicketPDF(b, profile); }
    catch (err) { console.error(err); alert("PDF failed. Run: npm i jspdf"); }
    finally { setDlId(null); }
  };

  return (
    <div>
      <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-6 text-yellow-700">My Campaigns</h2>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {!loading && error && <p className="text-center text-red-500 py-8">{error}</p>}
      {!loading && !error && bookings.length === 0 && (
        <p className="text-center text-amber-600 py-8">No campaign bookings found.</p>
      )}

      {!loading && !error && bookings.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {paged.map((b) => {
              const img = b.campaignId?.images?.[0] ? `${BASE_URL}${b.campaignId.images[0]}` : null;
              const isDL = dlId === b._id;
              return (
                <motion.div key={b._id}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl overflow-hidden border border-yellow-200 bg-yellow-50 hover:shadow-lg transition flex flex-col"
                >
                  {img
                    ? <img src={img} alt={b.campaignTitle} className="h-40 w-full object-cover" />
                    : <div className="h-40 flex items-center justify-center bg-yellow-100 text-yellow-700 font-semibold">No Image</div>
                  }
                  <div className="p-5 flex flex-col flex-1">
                    <p className="font-semibold text-yellow-700 mb-1 line-clamp-1">{b.campaignTitle}</p>
                    <p className="text-sm text-gray-600 mb-1">Tickets: {b.numberOfTickets}</p>
                    <p className="text-sm text-gray-600 mb-1">Total: ₹{b.totalAmount}</p>
                    <p className="text-xs text-gray-400 mb-3">
                      {formatDate(b.campaignStartDate)} – {formatDate(b.campaignEndDate)}
                    </p>
                    <span className={`text-xs px-3 py-1 rounded-full self-start mb-3 ${b.bookingStatus === "active" ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-700"}`}>
                      {b.bookingStatus === "active" ? "Active" : b.bookingStatus}
                    </span>

                    {/* ACTION BUTTONS */}
                    <div className="mt-auto flex gap-2">
                      {/* View Tickets */}
                      <button onClick={() => setSelected(b)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold transition hover:scale-105 active:scale-95"
                        style={{
                          background: "linear-gradient(135deg,#E8C96A,#C9A84C,#A8863A)",
                          color: "#3B2A0A", boxShadow: "0 2px 10px rgba(201,168,76,0.32)",
                        }}>
                        <FaTicketAlt style={{ fontSize: 9 }} />
                        View ({b.ticketIds?.length || 0})
                      </button>

                      {/* Download PDF directly */}
                      <motion.button
                        whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}
                        onClick={(e) => directDownload(e, b)}
                        disabled={isDL}
                        title="Download Ticket PDF"
                        className="flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl text-xs font-black transition disabled:opacity-60"
                        style={{
                          background: "linear-gradient(135deg,#FFF8E7,#FFF1CC)",
                          color: "#A8863A", border: "1.5px solid #E8C96A",
                          boxShadow: "0 2px 8px rgba(201,168,76,0.20)",
                          minWidth: 44,
                        }}
                      >
                        {isDL
                          ? <div className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                          : <><FaDownload style={{ fontSize: 10 }} /><span className="hidden sm:inline text-[10px]">PDF</span></>
                        }
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      <AnimatePresence>
        {selected && (
          <TicketsModal booking={selected} profile={profile} onClose={() => setSelected(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   FEEDBACK MODAL
───────────────────────────────────────────────────────── */
const FeedbackModal = ({ booking, onClose, onSubmitted }) => {
  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!feedback.trim()) { setError("Please write a review before submitting."); return; }
    const userId = sessionStorage.getItem("userId");
    setLoading(true); setError("");
    try {
      const res = await fetch(`${BASE_URL}/api/users/addfeedback/${userId}/${booking._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feedback: feedback.trim(), rating }),
      });
      const data = await res.json();
      if (data.success) { onSubmitted(booking._id); onClose(); }
      else setError(data.message || "Failed to submit.");
    } catch { setError("Network error."); }
    finally { setLoading(false); }
  };

  return createPortal(
    <motion.div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl border border-yellow-100"
        initial={{ scale: 0.85, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.85, y: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-md shrink-0">
            <FaTrophy className="text-white text-xl" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">Leave a Review</h3>
            <p className="text-xs text-gray-400 truncate max-w-[200px]">
              {booking.winningGift} · {booking.campaignTitle}
            </p>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-2 font-medium">Your Rating</p>
        <div className="flex gap-2 mb-5">
          {[1, 2, 3, 4, 5].map(s => (
            <button key={s} onClick={() => setRating(s)}
              className={`text-3xl transition-transform hover:scale-110 ${s <= rating ? "text-yellow-400" : "text-gray-200"}`}>
              ★
            </button>
          ))}
          <span className="ml-2 self-center text-sm text-yellow-600 font-semibold">{rating}/5</span>
        </div>
        <p className="text-sm text-gray-500 mb-2 font-medium">Your Review *</p>
        <textarea rows={4} placeholder="Share your experience about receiving this prize..."
          value={feedback} onChange={(e) => { setFeedback(e.target.value); setError(""); }}
          className="w-full px-4 py-3 rounded-2xl border border-yellow-200 outline-none text-gray-800 text-sm focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition resize-none mb-1"
        />
        <div className="flex justify-between mb-4">
          {error ? <p className="text-red-500 text-xs">{error}</p> : <span />}
          <p className={`text-xs ml-auto ${feedback.length < 5 ? "text-gray-400" : "text-green-500"}`}>
            {feedback.length} chars
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-yellow-200 text-yellow-700 font-semibold hover:bg-yellow-50 transition text-sm">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading}
            className="flex-1 py-3 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-yellow-500 to-amber-600 shadow hover:scale-[1.02] transition disabled:opacity-60 flex items-center justify-center gap-2">
            {loading
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Submitting…</>
              : "Submit Review ⭐"
            }
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

/* ─────────────────────────────────────────────────────────
   WINNINGS TAB
───────────────────────────────────────────────────────── */
const Winnings = ({ bookings, loading, error }) => {
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null);
  const [done, setDone] = useState(new Set());

  const wins = bookings.filter(b => b.winningGift);
  const totalPages = Math.ceil(wins.length / PER_PAGE);
  const paged = wins.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div>
      <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-6 text-yellow-700">My Winnings</h2>
      {loading && <div className="flex justify-center py-10"><div className="w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" /></div>}
      {!loading && error && <p className="text-center text-red-500 py-8">{error}</p>}
      {!loading && !error && wins.length === 0 && <p className="text-center text-amber-600 py-8">No winnings yet.</p>}

      {!loading && !error && wins.length > 0 && (
        <>
          <div className="space-y-4">
            {paged.map(w => (
              <motion.div key={w._id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="p-5 rounded-2xl border border-yellow-200 bg-gradient-to-br from-yellow-50 to-amber-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm hover:shadow-md transition">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-md shrink-0">
                    <FaTrophy className="text-white text-lg" />
                  </div>
                  <div>
                    <p className="font-bold text-yellow-800 text-base">{w.winningGift}</p>
                    <p className="text-sm text-gray-500">{w.campaignTitle}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{formatDate(w.bookingDate)}</p>
                  </div>
                </div>
                <div className="shrink-0">
                  {(done.has(w._id) || w.feedback)
                    ? <div className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-green-50 border border-green-200 text-green-600 text-sm font-semibold">
                      <FaCheckCircle /> Reviewed
                    </div>
                    : <button onClick={() => setModal(w)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-yellow-500 to-amber-600 text-white shadow hover:scale-105 transition">
                      ⭐ Leave Review
                    </button>
                  }
                </div>
              </motion.div>
            ))}
          </div>
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      <AnimatePresence>
        {modal && (
          <FeedbackModal booking={modal} onClose={() => setModal(null)}
            onSubmitted={id => setDone(p => new Set([...p, id]))} />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   PAYMENTS TAB
───────────────────────────────────────────────────────── */
const Payments = ({ bookings, loading, error }) => {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(bookings.length / PER_PAGE);
  const paged = bookings.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div>
      <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-6 text-yellow-700">My Payments</h2>
      {loading && <div className="flex justify-center py-10"><div className="w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" /></div>}
      {!loading && error && <p className="text-center text-red-500 py-8">{error}</p>}
      {!loading && !error && bookings.length === 0 && <p className="text-center text-amber-600 py-8">No payments found.</p>}

      {!loading && !error && bookings.length > 0 && (
        <>
          <div className="space-y-4">
            {paged.map(p => (
              <div key={p._id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-5 rounded-xl border border-yellow-200 bg-yellow-50">
                <span className="text-yellow-700 font-medium">{p.campaignTitle}</span>
                <span className="text-yellow-700 font-bold">₹{p.totalAmount}</span>
                <span className="text-sm text-gray-600">{formatDate(p.bookingDate)}</span>
              </div>
            ))}
          </div>
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   PROFILE STAT  &  PROFILE CARD
───────────────────────────────────────────────────────── */
const ProfileStat = ({ icon, label, value }) => (
  <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-100 rounded-2xl px-4 py-3">
    <div className="mt-0.5 text-yellow-500 text-base shrink-0">{icon}</div>
    <div className="min-w-0">
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="font-semibold text-yellow-800 text-sm truncate">{value}</p>
    </div>
  </div>
);

const ProfileCard = ({ profile, onProfileUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: profile.name || "", email: profile.email || "", phone: profile.phone || "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [saveOk, setSaveOk] = useState(false);
  // ── profile image state ──────────────────────────────
  const [imgPreview, setImgPreview] = useState(profile.profileImg ? `${BASE_URL}${profile.profileImg}` : null);
  const [imgUploading, setImgUploading] = useState(false);
  const [imgErr, setImgErr] = useState("");
  const imgInputRef = React.useRef(null);

  useEffect(() => {
    setForm({ name: profile.name || "", email: profile.email || "", phone: profile.phone || "" });
    setImgPreview(profile.profileImg ? `${BASE_URL}${profile.profileImg}` : null);
  }, [profile]);

  /* ── Upload profile image ── */
  const handleImgChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // instant local preview
    const localUrl = URL.createObjectURL(file);
    setImgPreview(localUrl);
    setImgErr("");
    setImgUploading(true);

    const uid = sessionStorage.getItem("userId");
    const fd = new FormData();
    fd.append("profileImg", file);

    try {
      const res = await fetch(`${BASE_URL}/api/users/uploadprofile-image/${uid}`, {
        method: "PUT",
        body: fd,
      });
      const data = await res.json();
      if (data.success) {
        const newUrl = data.profileImg ? `${BASE_URL}${data.profileImg}` : localUrl;
        setImgPreview(newUrl);
        onProfileUpdated({ ...profile, profileImg: data.profileImg });
      } else {
        setImgErr(data.message || "Upload failed.");
        setImgPreview(profile.profileImg ? `${BASE_URL}${profile.profileImg}` : null);
      }
    } catch {
      setImgErr("Network error during upload.");
      setImgPreview(profile.profileImg ? `${BASE_URL}${profile.profileImg}` : null);
    } finally {
      setImgUploading(false);
      // reset input so same file can be re-selected
      e.target.value = "";
    }
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required.";
    if (!form.phone.trim()) e.phone = "Phone is required.";
    else if (!/^\d{10}$/.test(form.phone)) e.phone = "10-digit number required.";
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    const uid = sessionStorage.getItem("userId");
    setSaving(true); setSaveErr(""); setSaveOk(false);
    try {
      const res = await fetch(`${BASE_URL}/api/users/updateprofile/${uid}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        onProfileUpdated({ ...profile, ...form });
        sessionStorage.setItem("userName", form.name.trim());
        sessionStorage.setItem("userEmail", form.email.trim());
        sessionStorage.setItem("userPhone", form.phone.trim());
        setSaveOk(true);
        setTimeout(() => { setEditing(false); setSaveOk(false); }, 1000);
      } else { setSaveErr(data.message || "Failed to update."); }
    } catch { setSaveErr("Network error."); }
    finally { setSaving(false); }
  };

  const ic = (field) =>
    `w-full px-3 py-2 rounded-xl border text-black text-sm outline-none transition ` +
    (errors[field]
      ? "border-red-300 focus:border-red-400 bg-red-50"
      : "border-yellow-200 focus:border-yellow-400 bg-white focus:ring-2 focus:ring-yellow-100");

  const initials = (editing ? form.name : profile.name)?.charAt(0).toUpperCase() || "U";

  return (
    <div className="h-full bg-white/80 backdrop-blur-xl rounded-3xl border border-yellow-200 shadow-lg p-5 sm:p-7">
      <div className="flex flex-col sm:flex-row items-center gap-6">

        {/* ── AVATAR with camera upload ── */}
        <div className="relative shrink-0 group">
          {/* hidden file input */}
          <input
            ref={imgInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImgChange}
          />

          {/* avatar circle */}
          <div
            className="w-20 h-20 rounded-full overflow-hidden shadow-lg border-2 border-yellow-300
              bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center
              text-white text-3xl font-bold"
          >
            {imgPreview ? (
              <img
                src={imgPreview}
                alt="Profile"
                className="w-full h-full object-cover"
                onError={() => setImgPreview(null)}
              />
            ) : (
              initials
            )}
          </div>

          {/* uploading spinner overlay */}
          {imgUploading && (
            <div className="absolute inset-0 rounded-full flex items-center justify-center"
              style={{ background: "rgba(0,0,0,0.45)" }}>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* camera button — always visible on mobile, hover on desktop */}
          <button
            onClick={() => imgInputRef.current?.click()}
            disabled={imgUploading}
            title="Change profile photo"
            className="absolute -bottom-1 -left-1 w-7 h-7 rounded-full flex items-center justify-center
              shadow-md border-2 border-white transition hover:scale-110 disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#C9A84C,#A8863A)" }}
          >
            <FaCamera style={{ color: "#fff", fontSize: 10 }} />
          </button>

          {/* edit / cancel toggle */}
          <button
            onClick={() => { setEditing(!editing); setErrors({}); setSaveErr(""); }}
            className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center
              shadow-md border-2 border-white transition hover:scale-110
              ${editing ? "bg-gray-400" : "bg-gradient-to-br from-yellow-400 to-amber-500"}`}
          >
            {editing ? <FaTimes className="text-white text-xs" /> : <FaEdit className="text-white text-xs" />}
          </button>
        </div>

        {/* upload error */}
        {imgErr && (
          <p className="text-xs text-red-500 font-medium sm:hidden -mt-2">{imgErr}</p>
        )}

        <div className="flex-1 w-full">
          {/* upload error (desktop) */}
          {imgErr && (
            <div className="hidden sm:block mb-3 px-4 py-2 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
              ⚠️ {imgErr}
            </div>
          )}

          {!editing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ProfileStat icon={<FaUser />} label="Name" value={profile.name} />
              <ProfileStat icon={<FaEnvelope />} label="Email" value={profile.email} />
              <ProfileStat icon={<FaPhone />} label="Phone" value={profile.phone} />
              <ProfileStat icon={<FaLayerGroup />} label="Total Plans" value={`${profile.totalPlans} Plans Purchased`} />
            </div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="w-full">
              <p className="text-sm font-semibold text-yellow-700 mb-4">Edit Profile</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                {[
                  { k: "name", lbl: "Full Name", t: "text", ph: "Your name" },
                  { k: "email", lbl: "Email", t: "email", ph: "you@email.com" },
                  { k: "phone", lbl: "Phone", t: "text", ph: "10-digit number" },
                ].map(({ k, lbl, t, ph }) => (
                  <div key={k}>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">{lbl}</label>
                    <input type={t} value={form[k]} placeholder={ph}
                      maxLength={k === "phone" ? 10 : undefined}
                      onChange={e => {
                        const v = k === "phone" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value;
                        setForm({ ...form, [k]: v }); setErrors({ ...errors, [k]: "" });
                      }}
                      className={ic(k)} />
                    {errors[k] && <p className="text-red-500 text-xs mt-1">{errors[k]}</p>}
                  </div>
                ))}
              </div>
              {saveErr && <div className="mb-3 px-4 py-2 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">⚠️ {saveErr}</div>}
              {saveOk && <div className="mb-3 px-4 py-2 rounded-xl bg-green-50 border border-green-200 text-green-600 text-sm flex items-center gap-2"><FaCheckCircle />Updated!</div>}
              <div className="flex gap-3">
                <button onClick={() => { setEditing(false); setErrors({}); setSaveErr(""); }}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-yellow-200 text-yellow-700 font-semibold text-sm hover:bg-yellow-50 transition">
                  <FaTimes />Cancel
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-yellow-500 to-amber-600 shadow hover:scale-[1.02] transition disabled:opacity-60">
                  {saving
                    ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</>
                    : <><FaSave />Save Changes</>
                  }
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   WALLET CARD
───────────────────────────────────────────────────────── */
const WalletCard = ({ walletBalance, onClick }) => (
  <motion.div onClick={onClick} whileHover={{ scale: 1.03, y: -3 }} whileTap={{ scale: 0.97 }}
    className="relative cursor-pointer overflow-hidden select-none h-full flex flex-col"
    style={{
      borderRadius: 24,
      background: "linear-gradient(170deg,#2C1500 0%,#6B3800 35%,#B87200 70%,#E8AA10 100%)",
      boxShadow: "0 8px 32px rgba(180,120,0,0.5),0 2px 8px rgba(0,0,0,0.22),inset 0 1px 0 rgba(255,220,80,0.2)",
      border: "1.5px solid rgba(255,210,60,0.3)",
    }}
  >
    <div className="absolute inset-0 pointer-events-none"
      style={{ background: "linear-gradient(135deg,transparent 25%,rgba(255,240,160,0.1) 50%,transparent 75%)", borderRadius: 24 }} />
    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none"
      style={{ background: "rgba(255,220,60,0.08)", border: "1.5px solid rgba(255,210,60,0.12)" }} />
    <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full pointer-events-none"
      style={{ background: "rgba(255,180,0,0.07)" }} />
    <div className="h-1 w-full flex-shrink-0"
      style={{ background: "linear-gradient(90deg,rgba(255,200,50,0.3),rgba(255,220,80,0.7),rgba(255,200,50,0.3))" }} />
    <div className="relative flex-1 flex flex-col items-center justify-center px-4 py-6 gap-4">
      <div className="flex items-center justify-center rounded-2xl"
        style={{ width: 52, height: 52, background: "rgba(255,220,60,0.14)", border: "1.5px solid rgba(255,210,60,0.28)", boxShadow: "0 4px 14px rgba(200,140,0,0.25)" }}>
        <FaWallet style={{ color: "#FFD966", fontSize: 22, filter: "drop-shadow(0 2px 6px rgba(255,200,0,0.6))" }} />
      </div>
      <p className="text-[10px] font-black uppercase tracking-widest text-center"
        style={{ color: "rgba(255,220,100,0.7)" }}>My Wallet</p>
      <div className="flex flex-row items-center gap-1">
        {walletBalance !== null
          ? <motion.span key={walletBalance} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
            className="text-3xl font-black leading-none tracking-tight"
            style={{ color: "#FFE566", textShadow: "0 2px 16px rgba(255,200,0,0.6)" }}>
            {walletBalance}
          </motion.span>
          : <span className="inline-block rounded-lg animate-pulse"
            style={{ width: 56, height: 32, background: "rgba(255,210,60,0.18)" }} />
        }
        <div className="p-2">
          <FaCoins style={{ color: "#FFD700", fontSize: 20, filter: "drop-shadow(0 0 5px rgba(255,215,0,0.9))" }} />
        </div>
      </div>
      <div className="flex items-center gap-1.5 px-3 py-1 rounded-full"
        style={{ background: "rgba(255,210,60,0.15)", border: "1px solid rgba(255,210,60,0.28)" }}>
        <span className="text-[10px] font-bold" style={{ color: "rgba(255,255,255,0.8)" }}>View</span>
        <FaArrowRight style={{ color: "#ffffff", fontSize: 9 }} />
      </div>
    </div>
    <div className="h-0.5 w-full flex-shrink-0"
      style={{ background: "linear-gradient(90deg,transparent,rgba(255,220,60,0.35),transparent)" }} />
  </motion.div>
);

/* ─────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────── */
const MyAccount = () => {
  const [activeTab, setActiveTab] = useState("campaigns");
  const [walletBalance, setWalletBalance] = useState(null);
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [bookingsError, setBookingsError] = useState("");
  const navigate = useNavigate();

  const tabs = [
    { id: "campaigns", label: "My Campaigns", icon: FaTicketAlt },
    { id: "winnings", label: "My Winnings", icon: FaTrophy },
    { id: "payments", label: "My Payments", icon: FaCreditCard },
  ];

  useEffect(() => {
    const uid = sessionStorage.getItem("userId");
    if (!uid) { setBookingsError("Not logged in."); setBookingsLoading(false); return; }

    fetch(`${BASE_URL}/api/users/mywallet/${uid}`)
      .then(r => r.json()).then(d => { if (d.success) setWalletBalance(d.walletBalance); }).catch(() => { });

    fetch(`${BASE_URL}/api/users/myprofile/${uid}`)
      .then(r => r.json()).then(d => { if (d.success) setProfile(d.profile); }).catch(() => { });

    fetch(`${BASE_URL}/api/users/mycampbooking/${uid}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) setBookings(d.bookings);
        else setBookingsError(d.message || "Failed to load bookings.");
      })
      .catch(() => setBookingsError("Network error."))
      .finally(() => setBookingsLoading(false));
  }, []);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-[#fffdf5] via-[#fff6d6] to-[#fffdf5] py-24 sm:py-14 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center sm:text-left mb-6
            bg-gradient-to-r from-yellow-600 to-yellow-800 bg-clip-text text-transparent">
            My Account
          </h1>

          {/* WALLET + PROFILE */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8 items-stretch">
            <div className="w-full sm:w-[30%] flex-shrink-0">
              <WalletCard walletBalance={walletBalance} onClick={() => navigate("/wallet")} />
            </div>
            <div className="flex-1">
              {profile && <ProfileCard profile={profile} onProfileUpdated={u => setProfile(u)} />}
            </div>
          </div>

          {/* TABS */}
          <div className="flex justify-center mb-10">
            <div className="flex flex-wrap justify-center gap-2 bg-white/70 backdrop-blur-xl border border-yellow-200 rounded-2xl sm:rounded-full p-2 shadow-lg w-full sm:w-auto">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    className="relative flex-1 sm:flex-none overflow-hidden px-4 sm:px-6 py-2 sm:py-3 rounded-full flex items-center justify-center gap-2 font-semibold transition">
                    {active && (
                      <motion.div layoutId="activeTab"
                        className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-500" />
                    )}
                    <span className={`relative z-10 flex items-center gap-2 text-sm sm:text-base ${active ? "text-gray-900 font-bold" : "text-yellow-700"}`}>
                      <Icon />{tab.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* CONTENT */}
          <div className="w-full flex justify-center px-3 sm:px-6 lg:px-8">
            <div className="w-full max-w-7xl bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 shadow-xl border border-yellow-200 overflow-hidden">
              {activeTab === "campaigns" && (
                <Campaigns bookings={bookings} loading={bookingsLoading} error={bookingsError} profile={profile} />
              )}
              {activeTab === "winnings" && (
                <Winnings bookings={bookings} loading={bookingsLoading} error={bookingsError} />
              )}
              {activeTab === "payments" && (
                <Payments bookings={bookings} loading={bookingsLoading} error={bookingsError} />
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default MyAccount;